import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  message,
  Spin,
  Empty,
  Row,
  Col,
  Alert,
  Statistic,
  Modal,
} from "antd";
import { EchartsRadar } from "../../../../components/echartsRadar/EchartsRadar";
import ReactECharts from "echarts-for-react";
import httpUtil from "../../../../utils/httpUtil";

const { Option } = Select;

// 生成学期选项的函数
const generateTermOptions = (enrollmentDate) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 月份1-12
  const enrollmentYear = new Date(enrollmentDate).getFullYear();
  const terms = [];

  // 生成所有可能的学期（直到当前年份）
  for (let year = enrollmentYear; year <= currentYear-1; year++) {
    // 第一学期（当年9月1日 - 次年1月31日）
    terms.push({
      term_id: `${year}-1`,
      term_name: `${year}学年第一学期`,
      start_date: `${year}-09-01`,
      end_date: `${year+1}-01-31` // 跨年到次年1月
    });
    
    // 第二学期（次年2月1日 - 次年8月31日）
    terms.push({
      term_id: `${year}-2`,
      term_name: `${year}学年第二学期`,
      start_date: `${year+1}-02-01`,
      end_date: `${year+1}-08-31` // 延长到8月31日
    });
  }
  // console.log("terms",terms);
  // 根据当前日期过滤学期
  if (currentMonth <= 1) {
    // 1月及之前：去掉最后一个学期数据
    return terms.slice(0, -1);
  } else {
    // 2月及之后：返回全部
    return terms;
  }
};

// 根据分数生成建议
const generateSuggestions = (data) => {
  if (!data || data.length === 0) return null;

  const suggestions = [];
  const sortedData = [...data].sort((a, b) => a.score - b.score);
  const weakAreas = sortedData.slice(0, 3);

  weakAreas.forEach((item) => {
    let suggestion = "";
    if (item.score < 60) {
      suggestion = `${item.name}得分较低(${Number(item.score).toFixed(
        3
      )}分)，建议加强相关学习和训练。`;
    } else if (item.score < 80) {
      suggestion = `${item.name}表现中等(${Number(item.score).toFixed(
        3
      )}分)，仍有提升空间。`;
    }
    if (suggestion) {
      suggestions.push(
        <Alert
          key={item.indicator_id}
          message={suggestion}
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
        />
      );
    }
  });

  const bestArea = sortedData[sortedData.length - 1];
  suggestions.push(
    <Alert
      key="best"
      message={`${bestArea.name}表现优异(${Number(bestArea.score).toFixed(
        3
      )}分)，继续保持！`}
      type="success"
      showIcon
    />
  );

  return suggestions;
};

export const ResultQuery = ({ userInfo }) => {
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [radarData, setRadarData] = useState([]);
  const [weightedScore, setWeightedScore] = useState(0);
  const [allResults, setAllResults] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentTermData, setCurrentTermData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  // 获取所有学期的评价结果
  const fetchAllResults = async () => {
    if (!userInfo?.username) return;

    setLoading(true);

    try {
      const params = { student_id: userInfo.username };
      httpUtil.getEvaluationResult(params).then((res) => {
        if (res.success) {
          setAllResults(res.data);

          // 设置默认选中的学期
          if (res.data.length > 0) {
            const defaultTermId = res.data[0].term_id;
            setSelectedTerm(defaultTermId);
            updateCurrentTermData(res.data[0]);
          }
        } else {
          message.error(res.error || "获取评价结果失败");
        }
        setLoading(false);
      });
    } catch (err) {
      console.error("获取评价结果失败:", err);
      message.error("获取数据失败，请稍后重试");
      setLoading(false);
    } 
  };

  // 更新当前学期数据
  const updateCurrentTermData = (termData) => {
    if (!termData) return;

    setCurrentTermData(termData);
    const formattedRadarData = termData.details.map((item) => ({
      ...item,
      score: Number(item.score),
    }));
    setRadarData(formattedRadarData);
    setWeightedScore(termData.total_score);
    setSuggestions(generateSuggestions(formattedRadarData));
  };

  // 处理折线图点击事件
  const handleChartClick = (params) => {
    const termId = params.data.term_id;
    const termData = allResults.find((item) => item.term_id === termId);

    if (termData) {
      updateCurrentTermData(termData);
      setModalVisible(true);
    }
    // console.log(terms);
  };

  const fetchEnrollmentDate = async () => {
    const params = { student_id: userInfo.username };
    setLoading(true);
    try {
      await httpUtil.getEnrollmentDateByStudent(params).then((res) => {
        if (res.success) {
          const enrollmentDate = new Date(res.data.enrollment_date)
            .toISOString()
            .split("T")[0];
            const newterms = generateTermOptions(enrollmentDate);
          setTerms(newterms);
          for (let i = 0; i < newterms.length; i++) {
            const generateParams = {
              student_id: userInfo.username,
              student_name: userInfo.full_name,
              term: newterms[i].term_id,
            };
            httpUtil.generateEvaluationResult(generateParams);
          }
          // const generateParams = {
          //   student_id: userInfo.username,
          //   student_name: userInfo.full_name,
          //   term: "2024-2",
          // };
          // httpUtil.generateEvaluationResult(generateParams);
        }
      });
    } catch (error) {
      console.error("获取评价结果失败:", error);
      message.error("获取数据失败，请稍后重试");
    } finally {
      fetchAllResults();
    }
  };
  // 初始化学期选项
  useEffect(() => {
    fetchEnrollmentDate();
  }, [userInfo]);

  // 折线图配置
  const getLineChartOption = () => {
    return {
      title: {
        text: "学期评价总分趋势",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          return `<div style="font-weight:bold">${params.data.term_name}</div>
                  <span>总分: </span><span style="font-weight:bold">${Number(params.data.value).toFixed(3)}</span>`;
        },
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: [10, 15],
        textStyle: {
          color: "#333",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: allResults.map((item) => item.term_name),
        axisLabel: {
          rotate: 30,
          margin: 15,
          color: "#000000",
          fontSize: 12,
        },
        axisLine: {
          lineStyle: {
            color: "#d9d9d9",
          },
        },
      },
      yAxis: {
        type: "value",
        name: "总分",
        min: 0,
        max: 100,
        nameTextStyle: {
          color: "#000000",
          fontSize: 12,
        },
        axisLabel: {
          rotate: 30,
          margin: 15,
          color: "#000000", // 坐标轴标签文字设为黑色
          fontSize: 12,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#d9d9d9",
          },
          color: "#000000",
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
          },
        },
      },
      series: [
        {
          data: allResults.map((item) => ({
            value: item.total_score,
            term_id: item.term_id,
            term_name: item.term_name,
          })),
          type: "line",
          smooth: true,
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#1890ff",
          },
          itemStyle: {
            color: "#1890ff",
            borderColor: "#fff",
            borderWidth: 2,
          },
          emphasis: {
            itemStyle: {
              color: "#ff4d4f",
              borderColor: "#fff",
              borderWidth: 2,
            },
          },
          label: {
            show: true,
            position: "top",
            formatter: (params) => Number(params.value).toFixed(1),
            color: "#1890ff",
            fontSize: 12,
            fontWeight: "bold",
          },
        },
      ],
    };
  };

  return (
    <div className="result-query-container">
      <Card
        title="历史评价结果"
        variant={false}
        loading={loading}
        style={{ marginTop: "24px" }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <ReactECharts
              option={getLineChartOption()}
              style={{ height: 400 }}
              onEvents={{
                click: handleChartClick,
              }}
              showLoading={loading}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={`${currentTermData?.term_name || ""} 评价详情`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1200}
      >
        {currentTermData && (
          <Row gutter={24}>
            <Col span={16}>
              <EchartsRadar
                data={radarData}
                title={`${
                  terms.find((t) => t.term_id === selectedTerm)?.term_name
                } 评价结果`}
                height={400}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="综合加权评分"
                value={weightedScore}
                precision={3}
                suffix="分"
                style={{ marginBottom: 24 }}
              />
              <Card
                title="分析建议"
                style={{ height: "calc(100% - 72px)", overflowY: "auto" }}
              >
                {suggestions && suggestions.length > 0 ? (
                  <div style={{ paddingRight: 8 }}>{suggestions}</div>
                ) : (
                  <Empty description="暂无特别建议" />
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};
